import { FamilyList } from "./components/FamilyList";
// TODO(account-hide): 付款方式區塊暫時隱藏，待實作後恢復。詳見 docs/account-hidden-sections.md
// import { AccountPaymentMethods } from "./components/AccountPaymentMethods";
import { OtherList } from "./components/OtherList";
import { RecentPayRecords } from "./components/RecentPayRecords";
// B 案：優惠券張數併入頂端摘要（AccountSummary），不再獨立成一個區塊
import { AccountSummary } from "./components/AccountSummary";
import { useEffect, useState } from "react";
import api, { handleRequest } from "@services/axios/apiManager";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser, IdentityContext } from "@utils/store/user/useUser";
import { YStack } from "tamagui";
import SectionLabel from "./components/ui/SectionLabel";
import { PlatformLegalInfo } from "@components/payment/TapPayCompliance";
import { PageLoadingBar } from "@components/loading/PageLoading";
import { buildXContextHeader } from "@utils/api/headers";
import { readUnpaidTotal, type UnpaidBillWidget } from "./unpaidBill";

interface AccountHomeData {
  widgets: {
    wallet?: number;
    coupons?: number;
    payment_methods?: number;
    points?: number;
    /** 待付金額（後端加總）；沒有待付的單時為 null */
    unpaid_bill?: UnpaidBillWidget | null;
  };
  customer?: {
    customer_name?: string;
    main_phone?: string;
  };
}

export const Account = () => {
  const { cusJwtToken } = useCusAuth();
  const { setUserData, chooseIdentity } = useUser();
  // 待付金額跟著目前選的身分（context）走：/customer/home 沒帶 X-Context 時後端會退回第一個 context，
  // 跟首頁、本月訂單（account-history 帶 X-Context）不一定是同一個。
  const xContext = buildXContextHeader(chooseIdentity);
  const [familyGroups, setFamilyGroups] = useState<IdentityContext[]>([]);
  // 換頁只用頁內細進度條，不拉全域遮罩（見 components/loading/PageLoading.tsx）
  const [loading, setLoading] = useState(false);

  const [resData, setResData] = useState<AccountHomeData | null>(null);
  /** resData 是哪個 context 的回應；換身分後、新回應回來前不拿舊的待付金額充數 */
  const [resContext, setResContext] = useState<string | undefined>(undefined);

  const fetchAll = async () => {
    setLoading(true);
    // /customer/contexts 一次回所有 context（含個人、家庭）。
    // 「我的瓦斯帳戶」直接帶入 API 回傳的所有 context，不再以 max_members 過濾。
    const [homeResult, contexts] = await Promise.allSettled([
      api.get("/app-api/customer/home", {
        headers: { Authorization: `Bearer ${cusJwtToken}`, ...(xContext ? { "X-Context": xContext } : {}) },
      }),
      api.get("/app-api/customer/contexts", {
        headers: { Authorization: `Bearer ${cusJwtToken}` },
      }),
    ]);
    // 兩支都回來（成功或失敗）就收掉進度條；只要有一支失敗，舊版會讓 loading 卡住不關。
    setLoading(false);
    if (homeResult.status === "fulfilled") {
      const { data } = homeResult.value.data;
      setResData(data);
      setResContext(xContext);
      setUserData({
        name: data?.customer?.customer_name,
        phone: data?.customer?.main_phone,
      });
    } else {
      console.log("home err", homeResult.reason);
    }

    if (contexts.status === "fulfilled") {
      const { data } = contexts.value.data;
      setFamilyGroups((data?.contexts ?? []) as IdentityContext[]);
    } else {
      console.log("contexts err", contexts.reason);
    }
  };
  useEffect(() => {
    if (cusJwtToken) {
      fetchAll();
    }
  }, [cusJwtToken, xContext]);

  // 尚未選定身分或回應還沒到：顯示「—」（null），不謊報 0
  const unpaidTotal = xContext && resData && resContext === xContext ? readUnpaidTotal(resData.widgets) : null;

  return (
    <>
      <PageLoadingBar visible={loading} />
      <AccountSummary unpaidTotal={unpaidTotal} />
      <FamilyList families={familyGroups} />
      {/* TODO(account-hide): 付款方式區塊暫時隱藏，待實作後恢復。詳見 docs/account-hidden-sections.md */}
      {/* <AccountPaymentMethods /> */}
      <OtherList />
      <RecentPayRecords />
      {/* TapPay 審核：帳號首頁底部即可看到公司／統編／客服／收費／取消 */}
      <YStack mt="$3" mb="$4">
        <SectionLabel title="關於平台" />
        <PlatformLegalInfo />
      </YStack>
    </>
  );
};

export default Account;
